import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMasterlistCheck } from '../../../../hooks/api/useMasterlistCheck';
import useFeatureFlags from '../../../../hooks/useFeatureFlag';
import { Journals } from '../../../../types/Journals.types';
import { JournalsTable } from './JournalsTable';
import {
  applyJournalFilter,
  checkFor,
  isStatusFilter,
  JournalFilter,
  masterlistIssues,
  statusLabel,
} from './masterlistCheck';
import { SearchField } from '../../../../components/inputs/SearchField';
import XLSX from '@e965/xlsx';
import { useToast } from '../../../../hooks/useToast';
import { AlertToast } from '../../../../components/AlertToast';
import { Agreements } from '../../../../types/Agreements.types';

interface JournalContainerProps {
  agreement: Agreements | undefined;
  isAdding: boolean | undefined;
  journals: Journals[];
  previewJournals: Journals[];
}
export const JournalsContainer: React.FC<JournalContainerProps> = ({
  agreement,
  isAdding,
  journals,
  previewJournals,
}: JournalContainerProps) => {
  const { t } = useTranslation();
  const { enableFilePreviews, enableMasterlistCheck } = useFeatureFlags();
  const { agreementId } = useParams<{ agreementId: string }>();
  const [filter, setFilter] = useState('');
  const [journalFilter, setJournalFilter] = useState<JournalFilter>('all');
  const { show: showExportErrorToast, trigger: triggerExportErrorToast } =
    useToast();

  const journalsInUse = useMemo(
    () => (previewJournals.length > 0 ? previewJournals : journals),
    [journals, previewJournals]
  );
  const filteredJournals = useMemo(() => {
    if (filter.trim() === '') return journalsInUse;
    const normalizedFilter = filter.trim().toLowerCase();
    return journalsInUse.filter(
      (j) =>
        j?.name?.toLowerCase().includes(normalizedFilter) ||
        j?.imprint?.toLowerCase().includes(normalizedFilter) ||
        j?.issn_e?.toLowerCase().includes(normalizedFilter) ||
        j?.issn_p?.toLowerCase().includes(normalizedFilter)
    );
  }, [filter, journalsInUse]);

  // The check covers the saved journals only; previews only exist while adding.
  const masterlist = useMasterlistCheck(
    agreementId,
    enableMasterlistCheck && !isAdding
  );
  const { masterlistCheck, checksByJournalId } = masterlist;

  // A status filter falls back to "all" while there's no check to filter by (flag off, loading,
  // failed), worked out here rather than in an effect so the select never renders a value it has
  // no option for. The user's choice is kept and applies again once the check arrives.
  const effectiveFilter =
    masterlistCheck || !isStatusFilter(journalFilter) ? journalFilter : 'all';

  // The summary counts cover the whole agreement, so a count shows all its journals: a search
  // left in the box would hide some of them.
  const showStatus = (status: JournalFilter) => {
    setFilter('');
    setJournalFilter(status);
  };

  // What the table shows; the count and the export follow it too.
  const visibleJournals = useMemo(
    () =>
      applyJournalFilter(filteredJournals, effectiveFilter, checksByJournalId),
    [filteredJournals, effectiveFilter, checksByJournalId]
  );

  // Announced, so a screen reader hears what the search or the "Show" filter left.
  const helperText = (
    <span role="status" aria-atomic="true">
      {visibleJournals.length === journalsInUse.length ? (
        <>Showing {journalsInUse.length} journals</>
      ) : (
        <>
          Showing {visibleJournals.length} out of {journalsInUse.length}{' '}
          journals
        </>
      )}
    </span>
  );

  const handleExport = () => {
    try {
      // SheetJS writes a raw \r as the literal "_x000D_" escape token instead
      // of a line break, so CRLF/CR line endings (e.g. from pasted text) must
      // be normalized to \n before the cell value reaches json_to_sheet.
      const normalizeLineBreaks = (value: string) =>
        value.replace(/\r\n?/g, '\n');

      const exportData = visibleJournals.map((journal: Journals) => {
        const check = checkFor(checksByJournalId, journal);
        return {
          Journal: journal.name,
          Imprint: journal.imprint || '',
          'ISSN-E': journal.issn_e || '',
          'ISSN-P': journal.issn_p || '',
          'Payment Option': journal.payment || '',
          License: journal.licenses?.join(', ') || '',
          Model: journal.publishing_model || '',
          Subjects: journal.subjects || '',
          URL: journal.url || '',
          'Extra Info': normalizeLineBreaks(journal.extra_info || ''),
          // The rows can be filtered by the check, so say why each one is there.
          ...(masterlistCheck && {
            Masterlist: check ? statusLabel(check, t) : '',
            'Masterlist issues': check
              ? masterlistIssues(check, journal, t).join('\n')
              : '',
          }),
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Journals');

      const agreementName = agreement ? agreement.name : '';
      const date = new Date().toISOString().split('T')[0];
      const fileName = `journals_${agreementName}_${date}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      triggerExportErrorToast();
      console.error('Export error:', error);
    }
  };

  if (!isAdding) {
    return (
      <Card variant="default">
        <Grid container spacing={3}>
          <Grid item md={12} xs={12}>
            <SearchField
              value={filter}
              onChange={setFilter}
              placeholder="Search"
              ariaLabel="Search for journals within the agreement"
              helperText={helperText}
              width={500}
            />
            {showExportErrorToast && (
              <AlertToast severity="error">
                There was an error exporting the journals. Please contact
                customer service.
              </AlertToast>
            )}
            <JournalsTable
              journals={visibleJournals}
              allJournals={journalsInUse}
              filter={effectiveFilter}
              onFilterChange={setJournalFilter}
              onSummaryFilter={showStatus}
              masterlist={masterlist}
              preview={false}
              onExport={handleExport}
              agreementName={agreement?.name ?? ''}
            />
          </Grid>
        </Grid>
      </Card>
    );
  } else if (previewJournals.length > 0 && enableFilePreviews) {
    return (
      <Card variant="default">
        <Grid container spacing={3}>
          <Grid item md={12} xs={12}>
            <SearchField
              value={filter}
              onChange={setFilter}
              placeholder="Search"
              ariaLabel="Search for journals within the agreement"
              helperText={helperText}
              width={500}
            />
            <JournalsTable
              journals={visibleJournals}
              allJournals={journalsInUse}
              filter={effectiveFilter}
              onFilterChange={setJournalFilter}
              onSummaryFilter={showStatus}
              masterlist={masterlist}
              preview={true}
            />
          </Grid>
        </Grid>
      </Card>
    );
  } else {
    return null;
  }
};
